import { Injectable, inject } from '@angular/core';
import { LogService } from './log.service';
import { StoreService } from './store.service';
import { Plan } from '@sp-shared';
import { StripeService } from 'ngx-stripe';
import { StripeElements } from '@stripe/stripe-js';
import { firstValueFrom, timeout, catchError, throwError } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class StripeCheckoutService {
	private readonly log = inject(LogService);
	private readonly storeService = inject(StoreService);

	/**
	 * Fetches client secret for a one-time payment intent
	 */
	async fetchClientSecretForPaymentIntent(planId: string): Promise<string> {
		try {
			this.log.debug('Fetching client secret for payment intent', { planId });
			const result = await this.storeService.createPaymentIntent(planId);
			if (!result.clientSecret) {
				throw new Error('Payment intent created but no client secret returned');
			}
			return result.clientSecret;
		} catch (error) {
			this.log.error('Error fetching client secret for payment intent:', error);
			throw new Error(this.handlePaymentError(error));
		}
	}

	/**
	 * Fetches client secret for a subscription
	 */
	async fetchClientSecretForSubscription(planId: string, billingInterval: 'month' | 'year' = 'month'): Promise<string> {
		try {
			this.log.debug('Fetching client secret for subscription', { planId, billingInterval });
			const result = await this.storeService.createSubscription(planId, billingInterval);
			if (!result.clientSecret) {
				throw new Error('Subscription created but no client secret returned');
			}
			return result.clientSecret;
		} catch (error) {
			this.log.error('Error fetching client secret for subscription:', error);
			throw new Error(this.handlePaymentError(error));
		}
	}

	/**
	 * Main method that routes to appropriate fetch method based on payment type
	 */
	async getClientSecret(
		paymentType: 'one-time' | 'subscription',
		planId: string,
		billingInterval: 'month' | 'year' = 'month'
	): Promise<string> {
		try {
			this.log.debug('Getting client secret', { paymentType, planId, billingInterval });
			if (paymentType === 'one-time') {
				return await this.fetchClientSecretForPaymentIntent(planId);
			} else {
				return await this.fetchClientSecretForSubscription(planId, billingInterval);
			}
		} catch (error) {
			this.log.error('Error getting client secret:', error);
			if (error instanceof Error) {
				throw error;
			}
			throw new Error(this.handlePaymentError(error));
		}
	}

	/**
	 * Validates and extracts planId from plan, throws error if invalid
	 */
	validatePlanId(plan: Plan | null): string {
		if (!plan) {
			throw new Error('No plan selected. Please select a plan to continue.');
		}
		if (!plan.id) {
			throw new Error('Selected plan is missing an ID. Please try selecting a different plan.');
		}
		return plan.id;
	}

	/**
	 * Handles payment confirmation logic using ngx-stripe
	 */
	async confirmPaymentWithStripe(
		stripeService: StripeService,
		elements: StripeElements,
		clientSecret: string,
		userName?: string | null,
		userEmail?: string | null,
		billingAddress?: {
			line1?: string;
			line2?: string;
			city?: string;
			state?: string;
			postal_code?: string;
			country?: string;
		} | null
	): Promise<unknown> {
		try {
			this.log.debug('Confirming payment with Stripe', { clientSecretPrefix: clientSecret.substring(0, 20) });

			// IMPORTANT: elements.submit() must be called before confirmPayment()
			// This validates the form and is required by Stripe
			// However, when using Payment Element with wallet support (Apple Pay, Google Pay),
			// card validation errors can occur even when a wallet payment method is selected.
			// We need to be lenient with card validation errors and let Stripe handle wallet payments.
			this.log.debug('Submitting elements for validation');
			const submitResult = await elements.submit();

			if (submitResult.error) {
				const errorCode = submitResult.error.code;
				const errorType = submitResult.error.type;
				const errorMessage = submitResult.error.message || 'Payment form validation failed';

				// Check if this is a card validation error about incomplete/missing card details
				// When using Payment Element with wallet support, these errors can occur even when
				// Apple Pay or other wallet methods are selected, because the card element is still
				// present and being validated. We should allow the payment to proceed and let Stripe
				// handle wallet payments, which don't require card details.
				const isIncompleteCardError =
					errorCode === 'incomplete_number' ||
					errorCode === 'incomplete_cvc' ||
					errorCode === 'incomplete_expiry' ||
					errorCode === 'incomplete_zip' ||
					(errorType === 'validation_error' &&
						(errorMessage.toLowerCase().includes('card details') ||
							errorMessage.toLowerCase().includes('fill in') ||
							errorMessage.toLowerCase().includes('complete')));

				// If it's an incomplete card error, it might be because a wallet payment is selected
				// Allow the payment to proceed - Stripe will handle wallet payments correctly
				// and will show an error if the user is actually trying to use card payment
				if (isIncompleteCardError) {
					this.log.debug(
						'Card validation error detected, but allowing payment to proceed (wallet payments may be selected)',
						{
							error: submitResult.error,
							errorCode,
							errorType,
							errorMessage
						}
					);
					// Don't throw - allow payment to proceed and let Stripe handle it
					// Stripe will show an error if card payment is actually required
				} else {
					// For other errors (invalid card, network errors, etc.), throw them
					this.log.error('Elements submission error', {
						error: submitResult.error,
						errorCode,
						errorType
					});
					throw new Error(errorMessage);
				}
			} else {
				this.log.debug('Elements submitted successfully, proceeding with payment confirmation');
			}

			// Build confirmParams with return_url and optional receipt_email
			// NOTE: return_url is required by Stripe, but Stripe will use embedded 3D Secure when possible
			// The return_url is only used if the card issuer requires a redirect (rare)
			// For most cards, authentication happens embedded in the modal
			const confirmParams: {
				return_url: string;
				receipt_email?: string;
				payment_method_data?: {
					billing_details: {
						name?: string;
						email?: string;
						address?: {
							line1?: string;
							line2?: string;
							city?: string;
							state?: string;
							postal_code?: string;
							country?: string;
						};
					};
				};
			} = {
				return_url: window.location.href
			};

			// Add receipt email if provided
			if (userEmail) {
				confirmParams['receipt_email'] = userEmail;
			}

			const hasBillingAddress = Boolean(
				billingAddress &&
				(billingAddress.line1 ||
					billingAddress.line2 ||
					billingAddress.city ||
					billingAddress.state ||
					billingAddress.postal_code ||
					billingAddress.country)
			);

			if (userName || userEmail || hasBillingAddress) {
				confirmParams.payment_method_data = {
					billing_details: {
						...(userName ? { name: userName } : {}),
						...(userEmail ? { email: userEmail } : {}),
						...(hasBillingAddress
							? {
									address: {
										...(billingAddress?.line1 ? { line1: billingAddress.line1 } : {}),
										...(billingAddress?.line2 ? { line2: billingAddress.line2 } : {}),
										...(billingAddress?.city ? { city: billingAddress.city } : {}),
										...(billingAddress?.state ? { state: billingAddress.state } : {}),
										...(billingAddress?.postal_code ? { postal_code: billingAddress.postal_code } : {}),
										...(billingAddress?.country ? { country: billingAddress.country } : {})
									}
								}
							: {})
					}
				};
			}

			// confirmPayment returns an Observable, convert to Promise
			this.log.debug('Calling stripeService.confirmPayment', {
				hasElements: !!elements,
				hasClientSecret: !!clientSecret,
				returnUrl: confirmParams.return_url
			});

			const confirmPaymentObservable = stripeService.confirmPayment({
				elements,
				clientSecret,
				confirmParams,
				redirect: 'if_required' as const
			});

			this.log.debug('confirmPayment Observable created, waiting for result...');

			// Add timeout (30 seconds) and error handling
			// If Stripe redirects for 3D Secure, the Observable might not complete immediately
			// Note: If Stripe redirects, the page will refresh and this code won't continue
			let result: unknown;
			try {
				result = await firstValueFrom(
					confirmPaymentObservable.pipe(
						timeout({
							first: 30000, // 30 second timeout
							with: () =>
								throwError(
									() =>
										new Error(
											'Payment confirmation timed out after 30 seconds. This may be due to 3D Secure authentication requiring a redirect.'
										)
								)
						}),
						catchError(error => {
							this.log.error('Error in confirmPayment Observable pipe', {
								error,
								errorType: typeof error,
								errorMessage: error?.message || String(error),
								errorStack: error?.stack
							});
							return throwError(() => error);
						})
					)
				);

				this.log.debug('confirmPayment Observable emitted successfully', { hasResult: !!result });

				// Log the full result structure for debugging
				this.log.debug('confirmPayment result', {
					result,
					resultType: typeof result,
					resultKeys: result && typeof result === 'object' ? Object.keys(result as Record<string, unknown>) : []
				});

				// Validate the result - Stripe's confirmPayment can return different structures
				if (result && typeof result === 'object') {
					// Cast through unknown first to avoid type assertion errors
					const resultObj = result as unknown as Record<string, unknown>;

					// Check for error in result
					if (resultObj['error']) {
						const error = resultObj['error'] as Record<string, unknown>;
						const errorMessage =
							typeof error['message'] === 'string' ? error['message'] : 'Payment confirmation failed';
						this.log.error('Payment confirmation returned error', { error });
						throw new Error(errorMessage);
					}

					// Check payment intent status - try multiple possible structures
					let paymentIntent: Record<string, unknown> | undefined;
					let status: string | undefined;

					// Try result.paymentIntent first
					paymentIntent = resultObj['paymentIntent'] as Record<string, unknown> | undefined;
					if (paymentIntent) {
						status = paymentIntent['status'] as string | undefined;
						this.log.debug('Found paymentIntent in result.paymentIntent', {
							id: paymentIntent['id'],
							status,
							keys: Object.keys(paymentIntent)
						});
					}

					// If not found, check if result itself is the payment intent
					if (!paymentIntent && resultObj['id'] && resultObj['status']) {
						paymentIntent = resultObj;
						status = resultObj['status'] as string | undefined;
						this.log.debug('Result itself appears to be payment intent', {
							id: resultObj['id'],
							status,
							keys: Object.keys(resultObj)
						});
					}

					if (paymentIntent && status) {
						this.log.debug('Payment intent status after confirmation', {
							status,
							paymentIntentId: paymentIntent['id']
						});

						if (status === 'succeeded') {
							this.log.info('Payment confirmed successfully', {
								paymentIntentId: paymentIntent['id'],
								status
							});
						} else if (status === 'requires_action' || status === 'requires_payment_method') {
							this.log.warn('Payment requires additional action', {
								paymentIntentId: paymentIntent['id'],
								status
							});
							// This is normal for 3D Secure - the user will be redirected
						} else if (status === 'processing') {
							this.log.info('Payment is processing', {
								paymentIntentId: paymentIntent['id'],
								status
							});
						} else {
							this.log.warn('Payment in unexpected status', {
								paymentIntentId: paymentIntent['id'],
								status
							});
						}
					} else {
						this.log.warn('Could not extract payment intent or status from result', {
							hasPaymentIntent: !!paymentIntent,
							hasStatus: !!status,
							resultKeys: Object.keys(resultObj)
						});
					}
				}

				return result;
			} catch (timeoutError) {
				const errorMessage =
					timeoutError instanceof Error
						? timeoutError.message
						: typeof timeoutError === 'string'
							? timeoutError
							: 'Unknown error';
				this.log.error('Timeout or error waiting for confirmPayment Observable', {
					error: timeoutError,
					errorType: typeof timeoutError,
					errorMessage
				});
				throw timeoutError;
			}
		} catch (error) {
			this.log.error('Error confirming payment with Stripe:', error);
			throw error;
		}
	}

	/**
	 * Extracts payment intent status from confirmPayment result
	 */
	getPaymentIntentStatus(result: unknown): { id?: string; status?: string } | null {
		if (!result || typeof result !== 'object') {
			return null;
		}

		try {
			const resultObj = result as unknown as Record<string, unknown>;
			const paymentIntent = resultObj['paymentIntent'] as Record<string, unknown> | undefined;
			if (paymentIntent) {
				return {
					id: paymentIntent['id'] as string | undefined,
					status: paymentIntent['status'] as string | undefined
				};
			}
		} catch (error) {
			this.log.debug('Error extracting payment intent status', { error });
		}

		return null;
	}

	/**
	 * Processes successful payment result
	 */
	handlePaymentSuccess(result: unknown): unknown {
		try {
			const statusInfo = this.getPaymentIntentStatus(result);
			this.log.debug('Handling payment success', {
				hasResult: !!result,
				paymentIntentId: statusInfo?.id,
				status: statusInfo?.status
			});
			return result;
		} catch (error) {
			this.log.error('Error handling payment success:', error);
			throw error;
		}
	}

	/**
	 * Processes payment errors and returns user-friendly messages
	 */
	handlePaymentError(error: unknown): string {
		try {
			this.log.error('Handling payment error:', error);

			if (error && typeof error === 'object') {
				const errorObj = error as Record<string, unknown>;
				const nestedError = errorObj['error'];
				if (typeof nestedError === 'string') {
					return nestedError;
				}
				if (nestedError && typeof nestedError === 'object') {
					const nestedErrorObject = nestedError as Record<string, unknown>;
					if (typeof nestedErrorObject['message'] === 'string') {
						return nestedErrorObject['message'];
					}
					if (typeof nestedErrorObject['error'] === 'string') {
						return nestedErrorObject['error'];
					}
				}
				if (typeof errorObj['message'] === 'string') {
					return errorObj['message'];
				}
			}

			if (typeof error === 'string') {
				return error;
			}

			if (error instanceof Error) {
				return error.message;
			}

			return 'An error occurred during payment. Please try again or contact support.';
		} catch (err) {
			this.log.error('Error handling payment error:', err);
			return 'An unexpected error occurred. Please try again or contact support.';
		}
	}
}
